
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export const useSubscriptionHandler = (profile: any) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async (subscriptionType: 'monthly' | 'lifetime') => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Please log in to subscribe");
        navigate("/login");
        return;
      }

      if (!profile?.id) {
        toast.error("Creator profile not found");
        return;
      }

      const amount = subscriptionType === 'monthly' ? 
        profile.subscription_price : 
        profile.lifetime_subscription_price;

      // Validate amount
      if (!amount || amount <= 0) {
        toast.error("Invalid subscription price");
        return;
      }

      console.log("Creating subscription:", {
        subscriptionType,
        creatorId: profile.id,
        amount,
        userId: user.id,
        currency: 'usd'
      });

      const { data, error: functionError } = await supabase.functions.invoke('handle-payment', {
        body: {
          subscriptionType,
          creatorId: profile.id,
          amount,
          currency: 'usd',
          userId: user.id,
          returnUrl: `${window.location.origin}/payment-confirmation?creator_id=${profile.id}&type=${subscriptionType}&amount=${Math.round(amount * 100)}`
        },
      });

      if (functionError) {
        console.error('Function error:', functionError);
        toast.error(functionError.message || 'Failed to process payment');
        return null;
      }

      if (!data) {
        console.error('No data returned from payment function');
        toast.error('Failed to create payment intent');
        return null;
      }

      const { clientSecret, error: backendError } = data;
      
      if (backendError) {
        console.error('Backend error:', backendError);
        toast.error(backendError);
        return null;
      }

      if (!clientSecret) {
        console.error('No client secret returned');
        toast.error('Failed to create payment intent');
        return null;
      }

      return clientSecret;

    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment. Please try again.');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async (creatorId: string) => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Please log in to cancel your subscription");
        navigate("/login");
        return false;
      }

      console.log("Canceling subscription:", {
        subscriberId: user.id,
        creatorId,
        timestamp: new Date().toISOString()
      });

      const { data, error } = await supabase.rpc('cancel_subscription', {
        subscriber_id: user.id,
        creator_id: creatorId
      });

      if (error) {
        console.error('Error canceling subscription:', error);
        toast.error('Failed to cancel subscription');
        return false;
      }

      console.log('Subscription cancellation result:', data);
      
      if (data === true) {
        toast.success('Subscription cancelled successfully');
        return true;
      } else {
        toast.error('No active subscription found');
        return false;
      }

    } catch (error) {
      console.error('Error in handleCancelSubscription:', error);
      toast.error('Failed to cancel subscription');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleSubscribe,
    handleCancelSubscription,
    isLoading
  };
};
